// Rechtslinks an einer Stelle (AE-1 Nachtrag 3, SPEC AE-14): Anmeldeseite und „Hilfe und Support“ im Profilmenü (bis
// Profil-Nachbau F-01 „Mehr erfahren“) nutzen dieselbe Prüfung. Nur konfigurierte, bestehende Rechtsseiten werden verlinkt; nie Platzhalter- oder Mustertexte (AE-3a).
// Die Werte kommen beim Bauen aus VITE_*-Variablen; Dockerfile, docker-compose.yml und die Deploy-Skripte reichen sie
// durch (Paket 12 K12-5, RSM-18). Seit Profil-Nachbau F-20 (PF-785) haben AGB und Datenschutzerklärung Fassungen in der
// Datenbank; mit veröffentlichter Fassung zeigen ihre Links auf /rechtliches/<slug> (rechtsAdresse unten).
import { useEffect, useMemo, useState } from 'react';
import apiClient from '../services/apiClient';

/** Nur eine absolute http- oder https-Adresse gilt; alles andere ergibt keinen Link. */
export function legalUrl(value: string | undefined): string | undefined {
  const url = value?.trim();
  if (!url) return undefined;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? parsed.href : undefined;
  } catch {
    return undefined;
  }
}

// F-23 (Nutzerblick Mitglied Z-05): der Anbieter von CLAiRIS, wie auf der Startseite (Dashboard.tsx). Schreibweise nach Bennys
// Antwort auf F-23-recht-1 (16.10.2026): „Solutions“ wie im Impressum auf aidea.tirol.
export const ANBIETER = 'Aidea Software Solutions GmbH';

type Umgebung = Partial<Record<'VITE_USAGE_POLICY_URL' | 'VITE_PRIVACY_URL' | 'VITE_TERMS_URL' | 'VITE_IMPRINT_URL' | 'VITE_DPA_URL' | 'VITE_SUPPORT_EMAIL', string>>;
export type Rechtslink = { key: string; label: string; href: string };
type Vorgabe = { key: string; label: string; name: keyof Umgebung };

function konfiguriert(liste: readonly Vorgabe[], env: Umgebung): Rechtslink[] {
  return liste.flatMap(({ key, label, name }) => {
    const href = legalUrl(env[name]);
    return href ? [{ key, label, href }] : [];
  });
}

// Reihenfolge wie im Menü: Nutzungsrichtlinien, Datenschutzerklärung, Nutzungsbedingungen (= AGB aus Paket 12).
// Plan 1a Welle 2, Ä-30 Punkt 4 (Frage F-09 (h), Standard ja; I, H-09): das Impressum steht auch nach der Anmeldung da,
// als letzter Rechtslink im Profilmenü – wie jeder andere nur mit gültiger Adresse (VITE_IMPRINT_URL). Das dreht
// die frühere Zusage AE-F13 („Impressum nur auf der Anmeldeseite“) um.
const MEHR_ERFAHREN = [
  { key: 'usagePolicy', label: 'Nutzungsrichtlinien', name: 'VITE_USAGE_POLICY_URL' },
  { key: 'privacy', label: 'Datenschutzerklärung', name: 'VITE_PRIVACY_URL' },
  { key: 'terms', label: 'Nutzungsbedingungen', name: 'VITE_TERMS_URL' },
  { key: 'imprint', label: 'Impressum', name: 'VITE_IMPRINT_URL' },
] as const;

// Seit F-23 nutzt „Dokumente“ hilfeRechtslinks.
export function mehrErfahrenLinks(env: Umgebung = import.meta.env): Rechtslink[] {
  return konfiguriert(MEHR_ERFAHREN, env);
}

// Paket 12 K12-5 (RSM-17, M-18.6): die Links der öffentlichen Seiten (Anmeldung, Registrierung, Einladung) in dieser
// Reihenfolge. Das Profilmenü nimmt diese Liste nicht; es führt seine eigene (MEHR_ERFAHREN oben), seit Ä-30 Punkt 4 mit
// dem Impressum am Ende.
const OEFFENTLICH = [
  { key: 'privacy', label: 'Datenschutz', name: 'VITE_PRIVACY_URL' },
  { key: 'imprint', label: 'Impressum', name: 'VITE_IMPRINT_URL' },
  { key: 'terms', label: 'AGB', name: 'VITE_TERMS_URL' },
] as const;

// Die öffentlichen Seiten nehmen aus legalLinks (unten, Paket 08 EIN-17) nur diese drei Schlüssel; AV-Vertrag und Support
// stehen im Bereich „Über“ (components/LegalLinks.tsx).
export const OEFFENTLICHE_SCHLUESSEL: readonly string[] = OEFFENTLICH.map(vorgabe => vorgabe.key);

// Profil-Nachbau F-20 und F-21 (PF-785; BAU-RECHT.md R-8, 4.1, 4.4): AGB, Datenschutzerklärung, Dokumentation zum Datenschutz
// und Nachweise haben Fassungen in der Datenbank und eine öffentliche Seite /rechtliches/<slug> (pages/Rechtstext.tsx). Mit
// veröffentlichter Fassung zeigt ein Link dorthin, sonst wie bisher auf die Umgebungsadresse (Rückfall; Dokumentation und
// Nachweise haben keinen). Impressum, AV-Vertrag und die Nutzungsrichtlinien von CLAiRIS bleiben Umgebungsadressen. Die Vorlage
// der Nutzungsrichtlinie ist nie öffentlich und steht deshalb nicht in der Liste.
export type RechtstextArt = 'agb' | 'datenschutz' | 'datenschutzDoku' | 'nachweise';
export type RechtstextFassung = { fassung: string; veroeffentlichtAm: string };
/** GET /api/administration/public/rechtstexte (BAU-RECHT 4.3): je Art die aktuelle veröffentlichte Fassung oder null. */
export type RechtstexteStand = Record<RechtstextArt, RechtstextFassung | null>;
type Rechtstextangabe = { art: RechtstextArt; slug: string; wort: string; umgebung?: 'VITE_TERMS_URL' | 'VITE_PRIVACY_URL' };

export const RECHTSTEXTE: readonly Rechtstextangabe[] = [
  { art: 'agb', slug: 'agb', wort: 'Vertragsbedingungen (AGB)', umgebung: 'VITE_TERMS_URL' },
  { art: 'datenschutz', slug: 'datenschutz', wort: 'Datenschutzerklärung', umgebung: 'VITE_PRIVACY_URL' },
  { art: 'datenschutzDoku', slug: 'datenschutz-doku', wort: 'Dokumentation zum Datenschutz' },
  { art: 'nachweise', slug: 'nachweise', wort: 'Nachweise' },
];
const RECHTSTEXT_PFAD = '/rechtliches/';
const angabe = (art: RechtstextArt) => RECHTSTEXTE.find(eintrag => eintrag.art === art)!;
/** Die Angaben zu einem Slug aus der Adresse; ein unbekannter Slug ergibt null. */
export const rechtstextVonSlug = (slug: string | undefined): Rechtstextangabe | null => RECHTSTEXTE.find(eintrag => eintrag.slug === slug) ?? null;
/** Die öffentliche Seite einer Art; mit fassung eine bestimmte, auch ältere Fassung. */
export const rechtstextPfad = (art: RechtstextArt, fassung?: string) =>
  `${RECHTSTEXT_PFAD}${angabe(art).slug}${fassung ? `?fassung=${encodeURIComponent(fassung)}` : ''}`;
/** Eine Adresse aus rechtsAdresse, die in der App bleibt (Link im selben Fenster) und nicht nach außen führt. */
export const istRechtsseite = (href: string) => href.startsWith(RECHTSTEXT_PFAD);

/** Die Antwort des Servers geprüft: Nur eine Fassung mit Bezeichnung und Datum zählt, alles andere gilt als „keine Fassung“. */
export function rechtstexteAus(daten: unknown): RechtstexteStand {
  const roh = (daten && typeof daten === 'object' ? daten : {}) as Record<string, unknown>;
  return Object.fromEntries(RECHTSTEXTE.map(({ art }) => {
    const wert = roh[art] as Partial<RechtstextFassung> | null | undefined;
    return [art, wert && typeof wert.fassung === 'string' && wert.fassung && typeof wert.veroeffentlichtAm === 'string'
      ? { fassung: wert.fassung, veroeffentlichtAm: wert.veroeffentlichtAm } : null];
  })) as RechtstexteStand;
}

// Zwischenspeicher: höchstens ein Abruf je Seitenaufruf. Scheitert er (etwa bei einem Server ohne Rechtstexte), bleibt es still
// beim Rückfall auf die Umgebung.
let rechtstexteStand: RechtstexteStand | null = null;
let rechtstexteAbruf: Promise<RechtstexteStand | null> | null = null;

export function ladeRechtstexte(): Promise<RechtstexteStand | null> {
  if (!rechtstexteAbruf) rechtstexteAbruf = apiClient.get<unknown>('/api/administration/public/rechtstexte')
    .then(antwort => (rechtstexteStand = rechtstexteAus(antwort.data)))
    .catch(() => null);
  return rechtstexteAbruf;
}

/** BAU-RECHT 4.4: /rechtliches/<slug> bei veröffentlichter Fassung, sonst die Umgebungsadresse (nur AGB und Datenschutzerklärung). */
export function rechtsAdresse(art: RechtstextArt, env: Umgebung = import.meta.env, stand: RechtstexteStand | null = rechtstexteStand): string | undefined {
  if (stand?.[art]) return rechtstextPfad(art);
  const name = angabe(art).umgebung;
  return name ? legalUrl(env[name]) : undefined;
}

// Paket 12 K12-7 (RSM-13, RSM-15, F12-11): Wortlaut der drei Pflicht-Haken der Vertragsannahme an dieser Stelle. AGB und
// Datenschutzerklärung verlinken nur mit Adresse aus rechtsAdresse (eigene Seite oder gültige VITE_TERMS_URL, VITE_PRIVACY_URL);
// sonst steht der Text ohne Link (M-14.8). Die Fassung nennt die Version, die der Server prüft. Die Haken nennen den
// Produktnamen nicht (MK-1). Seit F-23 ohne Adresse gesperrt (fehlt).
export type HakenKey = 'terms' | 'privacy' | 'business';
export type Vertragshaken = { key: HakenKey; vor: string; link: string; nach: string; href?: string; fehlt?: string };
type Versionen = { termsVersion: string | null; privacyVersion: string | null };
const fassung = (version: string | null) => (version ? ` (Fassung ${version})` : '');

// F-23 (Nutzerblick Firma H-04): ohne lesbaren Text kein Haken. Fehlt die Adresse (keine veröffentlichte Fassung, keine gültige
// VITE_TERMS_URL bzw. VITE_PRIVACY_URL), ist der Haken gesperrt, und dieser Satz steht darunter. Ohne Produktnamen wie die Haken.
export const HAKEN_FEHLT: Record<'terms' | 'privacy', string> = {
  terms: 'Die AGB sind noch nicht veröffentlicht. Annehmen können Sie sie, sobald der Anbieter sie veröffentlicht hat.',
  privacy: 'Die Datenschutzerklärung ist noch nicht veröffentlicht. Bestätigen können Sie sie, sobald der Anbieter sie veröffentlicht hat.',
};

export function vertragsHaken(versionen: Versionen, env: Umgebung = import.meta.env, stand: RechtstexteStand | null = rechtstexteStand): Vertragshaken[] {
  const agb = rechtsAdresse('agb', env, stand), datenschutz = rechtsAdresse('datenschutz', env, stand);
  return [
    { key: 'terms', vor: 'Ich akzeptiere die ', link: 'AGB', nach: `${fassung(versionen.termsVersion)}.`, href: agb, ...(agb ? {} : { fehlt: HAKEN_FEHLT.terms }) },
    { key: 'privacy', vor: 'Ich habe die ', link: 'Datenschutzerklärung', nach: `${fassung(versionen.privacyVersion)} gelesen.`, href: datenschutz, ...(datenschutz ? {} : { fehlt: HAKEN_FEHLT.privacy }) },
    { key: 'business', vor: 'Ich handle als Unternehmer und nicht als Verbraucher.', link: '', nach: '' },
  ];
}

// Paket 08 Schritt 8 (EIN-17): Rechtsseiten und Support für Anmeldung und Abschnitte, nicht für das Profilmenü
// (HINWEIS-FUER-08.md §4). Anmeldung, Registrierung und Einladung nehmen Datenschutz, Impressum und AGB
// (OEFFENTLICHE_SCHLUESSEL, Paket 12 RSM-17), „Über“ den AV-Vertrag und den Support.
// Support ist ein mailto-Link nur bei einer schlichten Adresse: kein Leerraum, kein ? und kein &, also keine Zusätze.
// PF-785: Datenschutz und AGB nehmen ihre Adresse aus rechtsAdresse (art), die übrigen weiter aus der Umgebung.
const RECHTSSEITEN = [
  { key: 'privacy', label: 'Datenschutz', name: 'VITE_PRIVACY_URL', art: 'datenschutz' },
  { key: 'imprint', label: 'Impressum', name: 'VITE_IMPRINT_URL' },
  { key: 'terms', label: 'AGB', name: 'VITE_TERMS_URL', art: 'agb' },
  { key: 'dpa', label: 'AVV', name: 'VITE_DPA_URL' },
] as const;
const MAILADRESSE = /^[^\s@?&]+@[^\s@?&]+\.[^\s@?&]+$/;

export function legalLinks(env: Umgebung = import.meta.env, stand: RechtstexteStand | null = rechtstexteStand): Rechtslink[] {
  const seiten: Rechtslink[] = RECHTSSEITEN.flatMap(vorgabe => {
    const href = 'art' in vorgabe ? rechtsAdresse(vorgabe.art, env, stand) : legalUrl(env[vorgabe.name]);
    return href ? [{ key: vorgabe.key, label: vorgabe.label, href }] : [];
  });
  const support = env.VITE_SUPPORT_EMAIL?.trim();
  return support && MAILADRESSE.test(support) ? [...seiten, { key: 'support', label: 'Support', href: `mailto:${support}` }] : seiten;
}

/** PF-785 (BAU-RECHT 4.4): die Rechtslinks für Komponenten. Lädt den Stand der Rechtstexte einmal je Seitenaufruf und zeichnet
 *  neu, sobald er da ist; bis dahin und ohne Antwort gelten die Umgebungsadressen. */
export function useRechtsLinks() {
  const [stand, setStand] = useState<RechtstexteStand | null>(rechtstexteStand);
  useEffect(() => {
    let aktiv = true;
    void ladeRechtstexte().then(neu => { if (aktiv) setStand(neu); });
    return () => { aktiv = false; };
  }, []);
  return useMemo(() => ({
    stand,
    adresse: (art: RechtstextArt) => rechtsAdresse(art, import.meta.env, stand),
    legalLinks: () => legalLinks(import.meta.env, stand),
    vertragsHaken: (versionen: Versionen) => vertragsHaken(versionen, import.meta.env, stand),
    hilfeRechtslinks: () => hilfeRechtslinks(import.meta.env, stand),
    fehlendeRechtstexte: () => fehlendeRechtstexte(import.meta.env, stand),
  }), [stand]);
}

/** F-23 (Nutzerblick Mitglied Z-05, Firma H-04; F-06): die Rechtstexte in „Hilfe und Feedback › Dokumente“, nur mit Adresse bzw.
 *  veröffentlichter Fassung, in dieser Reihenfolge. AGB und Datenschutzerklärung führen mit Fassung auf /rechtliches/<slug>
 *  (rechtsAdresse), sonst auf die Umgebungsadresse; Impressum und Nutzungsrichtlinien bleiben Umgebungsadressen. */
export function hilfeRechtslinks(env: Umgebung = import.meta.env, stand: RechtstexteStand | null = rechtstexteStand): Rechtslink[] {
  const eintraege: { key: string; label: string; href: string | undefined }[] = [
    { key: 'privacy', label: 'Datenschutzerklärung', href: rechtsAdresse('datenschutz', env, stand) },
    { key: 'terms', label: 'Vertragsbedingungen (AGB)', href: rechtsAdresse('agb', env, stand) },
    { key: 'privacyDocs', label: 'Dokumentation zum Datenschutz', href: rechtsAdresse('datenschutzDoku', env, stand) },
    { key: 'proofs', label: 'Nachweise', href: rechtsAdresse('nachweise', env, stand) },
    { key: 'usagePolicy', label: 'Nutzungsrichtlinien', href: legalUrl(env.VITE_USAGE_POLICY_URL) },
    { key: 'imprint', label: 'Impressum', href: legalUrl(env.VITE_IMPRINT_URL) },
  ];
  return eintraege.flatMap(({ key, label, href }) => (href ? [{ key, label, href }] : []));
}
/** F-23: was für den Betrieb noch fehlt (nur für Konten mit Plattform-Verwaltung angezeigt). */
export function fehlendeRechtstexte(env: Umgebung = import.meta.env, stand: RechtstexteStand | null = rechtstexteStand): string[] {
  return [
    rechtsAdresse('datenschutz', env, stand) ? null : 'Datenschutzerklärung',
    rechtsAdresse('agb', env, stand) ? null : 'Vertragsbedingungen (AGB)',
    legalUrl(env.VITE_IMPRINT_URL) ? null : 'Impressum',
  ].filter((wort): wort is string => wort !== null);
}
